import React from 'react'
import PropTypes from 'prop-types'
import { Alert, FlowGrid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import EventForm from './EventForm'

/**
 * PrimaryEventEdit
 *
 * Displays the primary event's edit fields expanded in place within the Events card.
 *
 * There is no nested Save/Cancel here: the primary event persists through the Events
 * card's single Save (see EventsCardEditMode), so this component only renders the
 * always-on shared-household warning and the event form. The "Edit Primary Event"
 * heading is rendered once, persistently, by EventsCardEditMode rather than here.
 *
 * @param {Object} props
 * @param {Object} props.event - Event field data
 * @param {number} props.index - Event index in field array
 * @param {Object} props.formArrayMethods - Methods from useFieldArray
 * @param {Function} [props.onEventRemoved] - Called with a removed event before it leaves the array
 * @param {Array} props.eventAutosuggestOptions - Options for the event type selector
 * @param {Object} props.person - Person data
 * @param {string} props.appName - Application name
 * @param {string} props.userId - User ID
 * @param {boolean} props.saveCardDataToSls - Whether to save to SLS
 * @param {string} props.splitScreenImageArk - Split screen image ARK
 * @returns {JSX.Element}
 */
const PrimaryEventEdit = ({
  event,
  index,
  formArrayMethods,
  onEventRemoved,
  eventAutosuggestOptions,
  person,
  appName,
  userId,
  saveCardDataToSls,
  splitScreenImageArk,
}) => {
  const [t] = useTranslation()

  return (
    <FlowGrid columns="1" gutters="xxs">
      <Alert type="warning" outlined dense>
        {t(
          'eventsForm.primary-event-edit-warning',
          'Editing this information will update the primary event for everyone in the household.'
        )}
      </Alert>
      <EventForm
        event={event}
        index={index}
        formArrayMethods={formArrayMethods}
        onEventRemoved={onEventRemoved}
        eventAutosuggestOptions={eventAutosuggestOptions}
        person={person}
        appName={appName}
        userId={userId}
        saveCardDataToSls={saveCardDataToSls}
        splitScreenImageArk={splitScreenImageArk}
      />
    </FlowGrid>
  )
}

PrimaryEventEdit.propTypes = {
  event: PropTypes.shape({}).isRequired,
  index: PropTypes.number.isRequired,
  formArrayMethods: PropTypes.shape({}).isRequired,
  onEventRemoved: PropTypes.func,
  eventAutosuggestOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  person: PropTypes.shape({}),
  appName: PropTypes.string,
  userId: PropTypes.string,
  saveCardDataToSls: PropTypes.bool,
  splitScreenImageArk: PropTypes.string,
}

export default PrimaryEventEdit
